import {
  InstantAnswerBuilderFn,
  InstantAnswerContext,
} from "@faire/help-center-self-serve/types";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";

import {
  MessageRetailerOrBrandButton,
  additionalDataRequiredForContactingRetailerOrBrandCTA,
} from "../../shared/instantAnswerUtils";
import { orderTermIsNet60 } from "../../utils/orderTermIsNet60";
import { orderWasAlreadyCharged } from "../../utils/orderWasAlreadyCharged";

import { getViewOrderInvoiceCTA } from "./shared";

export const CanceledItemsIA: InstantAnswerBuilderFn = (strictLocalize) => {
  return {
    titleBuilder: (_context) => {
      return INVOICE_UPDATES(strictLocalize);
    },

    descriptionBuilder: (context) => {
      return getDescription(context, strictLocalize);
    },

    primaryCtaBuilder: {
      textBuilder: (context) => {
        return getViewOrderInvoiceCTA(context, strictLocalize)?.label;
      },
      linkBuilder: (context) => {
        return getViewOrderInvoiceCTA(context, strictLocalize)?.getLink?.(
          context.brandOrder
        );
      },
    },
    secondaryCtaBuilder: {
      textBuilder: (context) => {
        return MessageRetailerOrBrandButton(context, strictLocalize)?.label;
      },
      onClickFunctionBuilder: (context) => {
        return MessageRetailerOrBrandButton(context, strictLocalize)?.onClick;
      },
    },
    additionalRequiredData:
      additionalDataRequiredForContactingRetailerOrBrandCTA(),
  };
};

const INVOICE_UPDATES = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage: "Invoice updates for cancelled items",
    description: {
      text: "Title for the answer of how order invoices work for cancelled items at Faire",
    },
  });

const getDescription = (
  context: InstantAnswerContext,
  strictLocalize: StrictLocalizeFunction
): string[] => {
  const { brandOrder } = context;
  if (!brandOrder) {
    return [CASE_FOR_OTHER_CONDITIONS(strictLocalize)];
  }

  if (orderTermIsNet60(brandOrder)) {
    return [CASE_WHEN_ORDER_HAS_NET60_TERMS(strictLocalize)];
  }

  if (orderWasAlreadyCharged(brandOrder)) {
    return [CASE_WHEN_ORDER_HAS_BEEN_CHARGED_ALREADY(strictLocalize)];
  }

  return [CASE_FOR_OTHER_CONDITIONS(strictLocalize)];
};

const CASE_WHEN_ORDER_HAS_NET60_TERMS = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "Since your selected order was placed using net 60 payment terms, the reported items will be removed from your invoice and you won’t be charged.",
    description: {
      text: "Description for an answer of how order invoices work for cancelled items at Faire",
    },
  });

const CASE_WHEN_ORDER_HAS_BEEN_CHARGED_ALREADY = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "Since your order was charged on shipment, we’ll refund the card on file for the amount of the reported items. Please allow usual processing time for the refund to show on your card.",
    description: {
      text: "Description for an answer of how order invoices work for cancelled items at Faire",
    },
  });

const CASE_FOR_OTHER_CONDITIONS = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage:
      "For orders placed using net 60 payment terms, the reported items will be removed from your invoice and you won’t be charged. For orders that were already charged on shipment, we’ll refund the card on file for the amount of the reported items.",
    description: {
      text: "Description for an answer of how order invoices work for cancelled items at Faire",
    },
  });
